/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useRef } from "react";
import { Search, DollarSign, Save, X, Clock } from "react-feather";
import {
  payoutService,
  PayoutRequest,
  PayoutUIStatus,
  PayoutStatus,
} from "../../../services/payout.service";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import PageHeader from "../../../components/common/PageHeader";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  DataTablePagination,
} from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { TableCard } from "@/components/ui/table-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BrandPayoutMethodsPrompt } from "@/components/brand/payouts/BrandPayoutMethodsPrompt";
import { PayoutAmount } from "@/components/payouts/PayoutAmount";

export function Payouts() {
  const navigate = useNavigate();
  // Set when the server refuses a payment because the brand has no payout
  // methods yet; opens the editor right here instead of sending them away.
  const [needsPayoutMethods, setNeedsPayoutMethods] = useState(false);
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<PayoutUIStatus | "">("");
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    Record<string, string>
  >({});
  const [editingPaymentMethod, setEditingPaymentMethod] = useState<
    string | null
  >(null);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);
  const [editedPaymentMethodId, setEditedPaymentMethodId] = useState<
    Record<string, string>
  >({});
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [togglingPayoutId, setTogglingPayoutId] = useState<string | null>(null);

  // Separate effect for the search query with debounce
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Main effect to handle data fetching
  useEffect(() => {
    fetchPayouts();
  }, [currentPage, pageSize, debouncedSearchQuery, statusFilter]);

  // Reset to page 1 when search or filter changes (but only if not already on page 1)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, statusFilter]);

  const fetchPayouts = async () => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      // Map UI status to backend status
      let backendStatus = "";
      if (statusFilter === "Pending") backendStatus = "PENDING";
      else if (statusFilter === "Paid") backendStatus = "PAID";
      // If statusFilter is '', backendStatus remains '' (fetch all)
      const response = await payoutService.getPayoutRequests(
        currentPage,
        pageSize,
        debouncedSearchQuery,
        backendStatus
      );
      setPayouts(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch {
      toast.error("Failed to load payout requests");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Filter payouts based on status (search is handled server-side)
  const filteredPayouts = useMemo(() => {
    return payouts.filter((payout) => {
      const matchesStatus =
        statusFilter === "" || payout.uiStatus === statusFilter;

      return matchesStatus;
    });
  }, [statusFilter, payouts]);


  // Calculate total amount of selected payouts (for future use)
  // const selectedTotalAmount = useMemo(() => {
  //   return payouts
  //     .filter((payout) => selectedPayouts.includes(payout.id))
  //     .reduce((sum, payout) => sum + payout.amount, 0);
  // }, [selectedPayouts, payouts]);

  const handleSelectAll = () => {
    if (selectedPayouts.length === filteredPayouts.length) {
      setSelectedPayouts([]);
    } else {
      setSelectedPayouts(
        filteredPayouts
          .filter((payout) => payout.status === "PENDING")
          .map((payout) => payout.id)
      );
    }
  };

  const handleSelectPayout = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();
    setSelectedPayouts((prev) =>
      prev.includes(id)
        ? prev.filter((payoutId) => payoutId !== id)
        : [...prev, id]
    );
  };

  const handleMarkAsPaid = async () => {
    if (selectedPayouts.length === 0) {
      toast.error("Please select at least one payout request");
      return;
    }

    // Check if all selected payouts have a payment method selected
    const missingPaymentMethods = selectedPayouts.filter(
      (id) => !selectedPaymentMethods[id]
    );
    if (missingPaymentMethods.length > 0) {
      toast.error("Please select a payment method for all selected payouts");
      return;
    }

    try {
      setIsProcessing(true);

      // Create payload with selected payment methods
      const payoutData = selectedPayouts.map((id) => ({
        payoutId: id,
        paymentMethodId: selectedPaymentMethods[id],
      }));

      await payoutService.bulkPayout(payoutData);

      // Clear selection
      setSelectedPayouts([]);
      setSelectedPaymentMethods({});

      // Refresh the list
      await fetchPayouts();

      toast.success("Payouts marked as paid successfully");
    } catch (error) {
      console.error("Failed to mark payouts as paid:", error);
      const data = (error as { response?: { data?: { code?: string; message?: string } } })
        ?.response?.data;
      if (data?.code === "BRAND_PAYMENT_METHODS_REQUIRED") {
        setNeedsPayoutMethods(true);
        toast.error(data.message || "Choose a payout method first");
      } else {
        toast.error(data?.message || "Failed to update payout status");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectPaymentMethod = (
    payoutId: string,
    paymentMethodId: string
  ) => {
    setSelectedPaymentMethods((prev) => ({
      ...prev,
      [payoutId]: paymentMethodId,
    }));
  };

  const handleEditPaymentMethod = (
    payoutId: string,
    currentPaymentMethodId: string
  ) => {
    setEditingPaymentMethod(payoutId);
    setEditedPaymentMethodId((prev) => ({
      ...prev,
      [payoutId]: currentPaymentMethodId,
    }));
  };

  const handleCancelEdit = () => {
    setEditingPaymentMethod(null);
    setEditedPaymentMethodId({});
  };

  const handleSavePaymentMethod = async (payoutId: string) => {
    try {
      setIsUpdatingPaymentMethod(true);
      const paymentMethodId = editedPaymentMethodId[payoutId];

      if (!paymentMethodId) {
        toast.error("Please select a payment method");
        return;
      }

      await payoutService.updatePayoutPaymentMethod(payoutId, paymentMethodId);
      toast.success("Payment method updated successfully");
      setEditingPaymentMethod(null);
      fetchPayouts(); // Refresh the data
    } catch (error) {
      toast.error("Failed to update payment method");
      console.error(error);
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const handleChangeEditedPaymentMethod = (
    payoutId: string,
    paymentMethodId: string
  ) => {
    setEditedPaymentMethodId((prev) => ({
      ...prev,
      [payoutId]: paymentMethodId,
    }));
  };

  const handleToggleStatus = async (
    payoutId: string,
    newStatus: PayoutStatus
  ) => {
    try {
      setIsTogglingStatus(true);
      setTogglingPayoutId(payoutId);

      if (newStatus === "PAID") {
        // When toggling from PENDING to PAID, use bulk payout service
        // Find the selected payment method for this payout
        const paymentMethodId = selectedPaymentMethods[payoutId];

        if (!paymentMethodId) {
          // If no payment method is selected, show an error
          toast.error("Please select a payment method first");
          return;
        }

        // Use bulk payout service with an array containing just this payout
        await payoutService.bulkPayout([
          {
            payoutId,
            paymentMethodId,
          },
        ]);
      } else {
        // When toggling from PAID to PENDING, use the existing togglePayoutStatus method
        await payoutService.togglePayoutStatus(payoutId, newStatus);
      }

      // Refresh the list to show updated status
      await fetchPayouts();

      toast.success(
        `Payout marked as ${
          newStatus === "PAID" ? "paid" : "pending"
        } successfully`
      );
    } catch (error) {
      console.error("Failed to toggle payout status:", error);
      toast.error("Failed to update payout status");
    } finally {
      setIsTogglingStatus(false);
      setTogglingPayoutId(null);
    }
  };

  const pendingCount = filteredPayouts.filter(
    (p) => p.status === "PENDING"
  ).length;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PayoutRequest, any>[] = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded"
          checked={
            selectedPayouts.length === pendingCount && pendingCount > 0
          }
          onChange={handleSelectAll}
          aria-label="Select all pending payouts"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedPayouts.includes(row.original.id)}
          onChange={(e) => handleSelectPayout(row.original.id, e)}
          disabled={row.original.status !== "PENDING"}
          className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded"
        />
      ),
      meta: {
        width: "w-10",
        skeleton: <div className="h-4 w-4 rounded bg-muted animate-pulse" />,
      },
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const payout = row.original;
        return (
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar
              src={payout.avatar}
              name={payout.name}
              className="w-8 h-8 shrink-0 text-xs"
            />
            <span className="break-words text-foreground font-medium">
              {payout.name}
            </span>
          </div>
        );
      },
      meta: {
        width: "w-48",
        skeleton: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "code",
      header: "Code",
      cell: ({ row }) => (
        <span className="block break-all text-muted-foreground">
          {row.original.code || "---"}
        </span>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "requested",
      header: "Date Requested",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {format(new Date(row.original.createdAt), "MM/dd/yyyy")}
        </span>
      ),
      meta: { width: "w-32" },
    },
    {
      id: "paid",
      header: "Date Paid",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.status === "PAID"
            ? format(new Date(row.original.updatedAt), "MM/dd/yyyy")
            : "---"}
        </span>
      ),
      meta: { width: "w-32" },
    },
    {
      id: "method",
      header: "Payment Method",
      cell: ({ row }) => {
        const payout = row.original;
        const infos = payout.matchingPaymentInfos ?? [];
        // The payment method currently in effect for this row: the edit
        // draft, the pending selection, or (for paid rows) the recorded one.
        const selectedId =
          editingPaymentMethod === payout.id && editedPaymentMethodId[payout.id]
            ? editedPaymentMethodId[payout.id]
            : payout.status === "PENDING" && selectedPaymentMethods[payout.id]
            ? selectedPaymentMethods[payout.id]
            : payout.status === "PAID"
            ? infos.find(
                (info) =>
                  info.paymentMethod.name.toLowerCase() ===
                  payout.paymentDetails?.paymentMethodName?.toLowerCase()
              )?.paymentMethod.id ?? infos[0]?.paymentMethod.id
            : undefined;
        return (
          <div data-row-click-exempt>
            <Select
              value={selectedId ?? ""}
              disabled={infos.length === 0}
              onValueChange={(paymentMethodId) => {
                if (editingPaymentMethod === payout.id) {
                  handleChangeEditedPaymentMethod(payout.id, paymentMethodId);
                } else if (payout.status === "PENDING") {
                  handleSelectPaymentMethod(payout.id, paymentMethodId);
                } else {
                  handleEditPaymentMethod(payout.id, paymentMethodId);
                  handleChangeEditedPaymentMethod(payout.id, paymentMethodId);
                }
              }}
            >
              <SelectTrigger className="capitalize bg-card">
                <SelectValue
                  placeholder={
                    payout.status === "PAID"
                      ? payout.paymentDetails?.paymentMethodName?.toLowerCase() ??
                        ""
                      : "Select method"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {infos.map((info) => (
                  <SelectItem
                    key={info.paymentMethod.id}
                    value={info.paymentMethod.id}
                    className="capitalize"
                  >
                    {info.paymentMethod.name.toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {editingPaymentMethod === payout.id && (
              <div className="flex mt-2 gap-2">
                <Button
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Save payment method"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSavePaymentMethod(payout.id);
                  }}
                  disabled={isUpdatingPaymentMethod}
                >
                  {isUpdatingPaymentMethod ? (
                    <Spinner size="sm" className="border-white" />
                  ) : (
                    <Save size={16} />
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Cancel edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  disabled={isUpdatingPaymentMethod}
                >
                  <X size={16} />
                </Button>
              </div>
            )}
          </div>
        );
      },
      meta: {
        width: "w-44",
        skeleton: <div className="h-9 w-full rounded-lg bg-muted animate-pulse" />,
      },
    },
    {
      id: "methodInfo",
      header: "Payment Method Info",
      cell: ({ row }) => {
        const payout = row.original;
        return (
          <span className="block break-all text-muted-foreground">
            {payout.status === "PENDING" &&
            payout.matchingPaymentInfos &&
            payout.matchingPaymentInfos.length > 0
              ? selectedPaymentMethods[payout.id]
                ? Object.values(
                    payout.matchingPaymentInfos.find(
                      (info) =>
                        info.paymentMethod.id ===
                        selectedPaymentMethods[payout.id]
                    )?.fieldData || {}
                  )[0] || "---"
                : "---"
              : editingPaymentMethod === payout.id &&
                editedPaymentMethodId[payout.id]
              ? Object.values(
                  payout.matchingPaymentInfos.find(
                    (info) =>
                      info.paymentMethod.id ===
                      editedPaymentMethodId[payout.id]
                  )?.fieldData || {}
                )[0] || "---"
              : payout.status === "PAID" &&
                payout.paymentDetails &&
                payout.paymentDetails.fieldData
              ? Object.values(payout.paymentDetails.fieldData)[0] || "---"
              : payout.status === "PAID" &&
                payout.matchingPaymentInfos &&
                payout.matchingPaymentInfos.length > 0
              ? Object.values(
                  payout.matchingPaymentInfos[0].fieldData
                )[0] || "---"
              : payout.affiliate?.email || "---"}
          </span>
        );
      },
      meta: { width: "w-56" },
    },
    {
      id: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <PayoutAmount
          amount={row.original.amount}
          breakdown={row.original.breakdown}
          className="text-foreground"
        />
      ),
      meta: { width: "w-24" },
    },
    {
      id: "markAs",
      header: "Mark as",
      cell: ({ row }) => {
        const payout = row.original;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleStatus(
                payout.id,
                payout.status === "PAID" ? "PENDING" : "PAID"
              );
            }}
            disabled={isTogglingStatus && togglingPayoutId === payout.id}
            className={`inline-flex items-center justify-center px-3 py-2 rounded text-sm font-medium cursor-pointer w-20 ${
              payout.status === "PAID"
                ? "bg-brand-600 text-white"
                : "bg-card border border-border text-muted-foreground"
            }`}
          >
            {isTogglingStatus && togglingPayoutId === payout.id ? (
              <Spinner size="sm" className="h-3 w-3 border-current mr-1" />
            ) : null}
            {payout.status === "PAID" ? "Paid" : "Pending"}
          </button>
        );
      },
      meta: {
        width: "w-28",
        skeleton: <div className="h-8 w-20 rounded bg-muted animate-pulse" />,
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payouts"
        customActions={[
          {
            id: "payouts-view-history",
            label: "View History",
            onClick: () => navigate("/brand/payouts/history"),
            variant: "primary" as const,
            icon: <Clock className="w-4 h-4" />,
          },
        ]}
      />

      <div className="space-y-6">
        {/* Brand-side moment of need: a payout is waiting and the brand has
            not said how it pays. Renders nothing once a method exists. */}
        <BrandPayoutMethodsPrompt
          when={needsPayoutMethods || payouts.some((p) => p.status === "PENDING")}
          title="Choose how you'll pay affiliates"
          body="An affiliate has requested a payout. Pick at least one payout method to mark it paid — affiliates can only be paid through a method you both support."
          forceOpen={needsPayoutMethods}
          onSaved={() => {
            setNeedsPayoutMethods(false);
            fetchPayouts();
          }}
        />

        {/* Filters and Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button
              onClick={handleMarkAsPaid}
              disabled={selectedPayouts.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <Spinner size="sm" className="border-white" />
              ) : (
                <DollarSign className="w-4 h-4" />
              )}
              Mark as Paid
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 md:ml-auto">
            <Select
              value={statusFilter === "" ? "all" : statusFilter}
              onValueChange={(value) => {
                setStatusFilter(
                  value === "all" ? "" : (value as PayoutUIStatus)
                );
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[200px]">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                type="text"
                className="pl-10"
                placeholder="Search by name, code, or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <TableCard>
          <DataTable
            columns={columns}
            data={filteredPayouts}
            isLoading={isLoading}
            emptyText="No payout requests available"
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              navigate(`/brand/affiliates/${row.affiliateId}`)
            }
          />
          <DataTablePagination
            page={currentPage}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo(0, 0);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            noun="payouts"
          />
        </TableCard>
      </div>
    </div>
  );
}
